import React from 'react';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import { MobileFilterFooter } from '../MobileFilterFooter';
import { MobileFilterHeader } from '../MobileFilterHeader';
import { SearchFilterTrigger } from '../SearchFilterTrigger';
import baseFilterStyles from '../baseFilterStyles.module.css';

import { SizeBox } from './SizeBox';
import { SizeFilterRow } from './SizeFilterRow';
import styles from './styles.module.css';

import { Dropdown } from '@/components/Dropdown';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { pruneEmptySizes } from '@/modules/filters/pruneEmptySizes';
import { selectBranchFromDepartment } from '@/modules/filters/selectBranchFromDepartment';
import { FilterTypes } from '@/modules/filters/types';
import { useSizeFilters } from '@/modules/filters/useSizeFilters';
import { useSearchFilterAggregates } from '@/modules/search/useSearchFilterAggregates';
import { useSearchFilters } from '@/modules/search/useSearchFilters';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Props = {
  closeMobileFilterDrawer: () => void;
  sellerId?: number;
};

export function SizeFilter({ closeMobileFilterDrawer, sellerId }: Props) {
  const t = useTranslations('common');
  const [, user] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({ userId: user?.id });

  const title = t('Size');

  const { data: sizeFilterTree, isLoading } = useSizeFilters();

  const { searchFilters, setSearchFilters } = useSearchFilters();

  const { data: aggregates } = useSearchFilterAggregates({
    sellerId,
  });
  const isSizeFilterDisabled = isLoading || !sizeFilterTree?.data?.length;

  function handleSizeBoxClick(value: string) {
    setSearchFilters.setSizeFilter(value);
  }

  const prunedSizeFilterTree =
    isSizeFilterDisabled || !aggregates
      ? []
      : pruneEmptySizes(sizeFilterTree.data, aggregates.data.sizes ?? {});

  const sizeTree = selectBranchFromDepartment({
    sizeFilterTree: prunedSizeFilterTree,
    gender: searchFilters.gender,
    isKids: searchFilters.isKids,
  });

  return (
    <Dropdown
      TriggerComponent={(props) => (
        <SearchFilterTrigger
          {...props}
          isActive={Boolean(searchFilters.sizes)}
          title={title}
          disabled={isSizeFilterDisabled}
        />
      )}
      onOpen={() =>
        sendActivityTrackerEvent(
          ActivityTrackerEventType.FILTER_OPENED_ACTION,
          {
            filterType: FilterTypes.Size,
          }
        )
      }
    >
      {({ className, toggleDropdown }) => (
        <div
          className={clsx(
            className,
            {
              [baseFilterStyles.searchFilterDropdownContents]:
                !isSizeFilterDisabled,
            },
            styles.sizeFilterContents
          )}
        >
          <MobileFilterHeader
            title={title}
            closeMobileFilterDrawer={closeMobileFilterDrawer}
            toggleDropdown={toggleDropdown}
          />
          {
            <div className={baseFilterStyles.content}>
              {sizeTree.map(({ id, name, children }) => {
                return (
                  <SizeFilterRow
                    key={`${id}-${name}`}
                    name={name}
                    // eslint-disable-next-line react/no-children-prop
                    children={children}
                    Leaf={({
                      name,
                      value,
                    }: {
                      name: string;
                      value: string;
                    }) => (
                      <SizeBox
                        name={name}
                        value={value}
                        isSelected={Boolean(
                          searchFilters.sizes?.includes(value)
                        )}
                        handleClick={handleSizeBoxClick}
                      />
                    )}
                  />
                );
              })}
            </div>
          }
          <MobileFilterFooter
            handleSubmit={() => {
              toggleDropdown();
              closeMobileFilterDrawer();
            }}
            handleClear={() => setSearchFilters.setSizeFilter()}
            sellerId={sellerId}
          />
        </div>
      )}
    </Dropdown>
  );
}
